import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Search, Building2, AlertTriangle, CheckCircle2 } from 'lucide-react';

import { MasterToolbar }          from '@/components/master/MasterToolbar';
import { MasterAuditInfo }        from '@/components/master/MasterAuditInfo';
import { MasterSearchModal }      from '@/components/master/MasterSearchModal';
import type { SearchColumn }      from '@/components/master/MasterSearchModal';
import { MasterReportPreview }    from '@/components/master/report/MasterReportPreview';
import { ReportHeader }           from '@/components/master/report/ReportHeader';
import { ReportSection }          from '@/components/master/report/ReportSection';
import { ReportFooter }           from '@/components/master/report/ReportFooter';
import { ConfirmDialog }          from '@/components/ui/ConfirmDialog';
import { Input }                  from '@/components/ui/Input';
import { Combobox }               from '@/components/ui/Combobox';
import { Badge }                  from '@/components/ui/Badge';
import { Spinner }                from '@/components/ui/Spinner';
import { Card }                   from '@/components/ui/Card';

import { useAuth }                from '@/hooks/useAuth';
import { usePermission }          from '@/hooks/usePermission';
import { useMasterScreenState }   from '@/hooks/useMasterScreenState';
import { ScreenModeBadge } from '@/components/master/ScreenModeBadge';
import { Permissions }            from '@/constants/permissions';
import { AppRoutes }              from '@/constants/routes';
import { companyApi }             from '@/api/company.api';
import { CURRENCY_OPTIONS }       from '@/data/currencies';
import { TIMEZONE_OPTIONS }       from '@/data/timezones';
import type { CompanyDto, CompanyListDto } from '@/types/company.types';
import { cn } from '@/utils/cn';

// ── Zod schema ─────────────────────────────────────────────────────────────────

const schema = z.object({
  code:                 z.string().min(1, 'Code is required').max(20)
                          .regex(/^[A-Za-z0-9_-]+$/, 'Only letters, digits, - and _ allowed'),
  name:                 z.string().min(1, 'Name is required').max(200),
  legalName:            z.string().max(200).nullable().optional(),
  registrationNumber:   z.string().max(50).nullable().optional(),
  taxNumber:            z.string().max(50).nullable().optional(),
  baseCurrencyCode:     z.string().min(3, 'Currency is required').max(3),
  timeZone:             z.string().nullable().optional(),
  fiscalYearStartMonth: z.coerce.number().int().min(1).max(12),
  chequeNumberMode:     z.enum(['AUTO', 'MANUAL']),
  addressLine1:         z.string().max(200).nullable().optional(),
  addressLine2:         z.string().max(200).nullable().optional(),
  city:                 z.string().max(100).nullable().optional(),
  countryCode:          z.string().max(3).nullable().optional(),
  phone:                z.string().max(30).nullable().optional(),
  email:                z.string().email('Invalid email').or(z.literal('')).nullable().optional(),
  website:              z.string().max(200).nullable().optional(),
  isActive:             z.boolean(),
});

type FormValues = z.infer<typeof schema>;

// ── Constants ─────────────────────────────────────────────────────────────────

const MONTHS = [
  { value: '1',  label: 'January'   }, { value: '2',  label: 'February'  },
  { value: '3',  label: 'March'     }, { value: '4',  label: 'April'     },
  { value: '5',  label: 'May'       }, { value: '6',  label: 'June'      },
  { value: '7',  label: 'July'      }, { value: '8',  label: 'August'    },
  { value: '9',  label: 'September' }, { value: '10', label: 'October'   },
  { value: '11', label: 'November'  }, { value: '12', label: 'December'  },
];

const MONTH_NAMES = ['', 'January','February','March','April','May','June',
  'July','August','September','October','November','December'];

const SEARCH_COLUMNS: SearchColumn<CompanyListDto>[] = [
  { key: 'code',             label: 'Code',     className: 'w-28 font-mono' },
  { key: 'name',             label: 'Name' },
  { key: 'baseCurrencyCode', label: 'Currency', className: 'w-20 text-center' },
  { key: 'city',             label: 'City',     className: 'w-28', render: r => r.city ?? '—' },
  { key: 'isActive', label: 'Status', className: 'w-20',
    render: r => <Badge variant={r.isActive ? 'success' : 'default'}>{r.isActive ? 'Active' : 'Inactive'}</Badge> },
];

const EMPTY_FORM: FormValues = {
  code: '', name: '', legalName: '', registrationNumber: '', taxNumber: '',
  baseCurrencyCode: 'USD', timeZone: 'UTC', fiscalYearStartMonth: 1,
  chequeNumberMode: 'MANUAL',
  addressLine1: '', addressLine2: '', city: '', countryCode: '',
  phone: '', email: '', website: '', isActive: true,
};

/** Where a cheque number on a payment voucher comes from (GAP-001). */
const CHEQUE_NUMBER_MODES = [
  { value: 'MANUAL', label: 'Manual — the operator types the cheque number' },
  { value: 'AUTO',   label: 'Automatic — take the next number from a registered cheque book' },
];

const TABS = ['General', 'Contact', 'Tax & Registration', 'Finance', 'Audit'] as const;
type Tab = (typeof TABS)[number];

// ── Helpers ────────────────────────────────────────────────────────────────────

function dtoToForm(dto: CompanyDto): FormValues {
  return {
    code:                 dto.code,
    name:                 dto.name,
    legalName:            dto.legalName             ?? '',
    registrationNumber:   dto.registrationNumber    ?? '',
    taxNumber:            dto.taxNumber             ?? '',
    baseCurrencyCode:     dto.baseCurrencyCode,
    timeZone:             dto.timeZone              ?? 'UTC',
    fiscalYearStartMonth: dto.fiscalYearStartMonth,
    chequeNumberMode:     dto.chequeNumberMode      ?? 'MANUAL',
    addressLine1:         dto.addressLine1          ?? '',
    addressLine2:         dto.addressLine2          ?? '',
    city:                 dto.city                 ?? '',
    countryCode:          dto.countryCode           ?? '',
    phone:                dto.phone                ?? '',
    email:                dto.email                ?? '',
    website:              dto.website              ?? '',
    isActive:             dto.isActive,
  };
}

function nullify(v: string | null | undefined): string | null {
  return v?.trim() ? v.trim() : null;
}

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// ── Component ─────────────────────────────────────────────────────────────────

export const CompanyMasterPage = () => {
  const navigate = useNavigate();
  const { user }          = useAuth();
  const { hasPermission } = usePermission();

  const canView   = hasPermission(Permissions.ADMIN_COMPANY_VIEW);
  const canCreate = hasPermission(Permissions.ADMIN_COMPANY_CREATE);
  const canEdit   = hasPermission(Permissions.ADMIN_COMPANY_EDIT);
  const canDelete = hasPermission(Permissions.ADMIN_COMPANY_DELETE);

  const screen = useMasterScreenState({ canEdit, canDelete });

  // ── Refs ─────────────────────────────────────────────────────────────────────
  const codeInputRef = useRef<HTMLInputElement>(null);

  // ── Local state ──────────────────────────────────────────────────────────────
  const [record,       setRecord]       = useState<CompanyDto | null>(null);
  const [codeInput,    setCodeInput]    = useState('');
  const [activeTab,    setActiveTab]    = useState<Tab>('General');
  const [searchOpen,   setSearchOpen]   = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [lookupBusy,   setLookupBusy]   = useState(false);
  const [flashMsg,     setFlashMsg]     = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [printOpen,    setPrintOpen]    = useState(false);

  // Pending-save data — set after form validation passes, before user confirms
  const [pendingSave,  setPendingSave]  = useState<FormValues | null>(null);

  // Confirmation dialogs
  const [showSaveConfirm,   setShowSaveConfirm]   = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showNewConfirm,    setShowNewConfirm]     = useState<string | null>(null); // holds the code

  // ── Form ─────────────────────────────────────────────────────────────────────
  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: EMPTY_FORM,
  });

  const isActive = watch('isActive');

  // ── Flash helper ─────────────────────────────────────────────────────────────
  const flash = (type: 'success' | 'error', text: string) => {
    setFlashMsg({ type, text });
    setTimeout(() => setFlashMsg(null), 5000);
  };

  // ── Reset screen to INITIAL blank state ───────────────────────────────────────
  const resetToInitial = useCallback(() => {
    setRecord(null);
    setCodeInput('');
    reset(EMPTY_FORM);
    screen.setMode('initial');
    setActiveTab('General');
    setPendingSave(null);
    // Focus code input after reset
    setTimeout(() => codeInputRef.current?.focus(), 50);
  }, [reset, screen]);

  // ── Load a DTO into the form ──────────────────────────────────────────────────
  const loadRecord = useCallback((dto: CompanyDto) => {
    setRecord(dto);
    reset(dtoToForm(dto));
    setCodeInput(dto.code);
    screen.setMode('view');
    setActiveTab('General');
  }, [reset, screen]);

  // ── Code lookup ───────────────────────────────────────────────────────────────
  const handleCodeLookup = async () => {
    const code = codeInput.trim().toUpperCase();
    if (!code) return;

    if (!canView) {
      flash('error', 'You do not have permission to view companies.');
      return;
    }

    setLookupBusy(true);
    try {
      const res = await companyApi.getByCode(code);
      if (res.data.data) {
        loadRecord(res.data.data);
      }
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 404) {
        if (!canCreate) {
          flash('error', `Company code '${code}' was not found.`);
          return;
        }
        // Show styled confirmation dialog (not window.confirm)
        setShowNewConfirm(code);
      } else {
        flash('error', 'Lookup failed. Please try again.');
      }
    } finally {
      setLookupBusy(false);
    }
  };

  // ── Confirm: create new from code ────────────────────────────────────────────
  const handleConfirmCreateNew = () => {
    const code = showNewConfirm;
    setShowNewConfirm(null);
    if (!code) return;
    reset({ ...EMPTY_FORM, code });
    setCodeInput(code);
    setRecord(null);
    screen.setMode('new');
    setActiveTab('General');
  };

  // ── Toolbar: Refresh → always reset to INITIAL blank state ───────────────────
  const handleRefresh = () => {
    resetToInitial();
  };

  // ── Toolbar: Edit ─────────────────────────────────────────────────────────────
  const handleEdit = () => {
    screen.setMode('edit');
  };

  // ── Toolbar: Save (step 1 — validate first, then confirm) ────────────────────
  const handleSaveClick = () => {
    handleSubmit((validData) => {
      // Form is valid — store payload and show confirmation
      setPendingSave(validData);
      setShowSaveConfirm(true);
    })();
  };

  // ── Confirm save (step 2 — actually save) ─────────────────────────────────────
  const executeSave = async () => {
    if (!pendingSave) return;
    setShowSaveConfirm(false);
    setSaving(true);
    try {
      const data = pendingSave;
      const payload = {
        code:                 data.code.toUpperCase(),
        name:                 data.name.trim(),
        legalName:            nullify(data.legalName),
        registrationNumber:   nullify(data.registrationNumber),
        taxNumber:            nullify(data.taxNumber),
        baseCurrencyCode:     data.baseCurrencyCode,
        timeZone:             nullify(data.timeZone),
        fiscalYearStartMonth: data.fiscalYearStartMonth,
        chequeNumberMode:     data.chequeNumberMode,
        addressLine1:         nullify(data.addressLine1),
        addressLine2:         nullify(data.addressLine2),
        city:                 nullify(data.city),
        countryCode:          nullify(data.countryCode),
        phone:                nullify(data.phone),
        email:                nullify(data.email),
        website:              nullify(data.website),
      };

      if (screen.isNew) {
        const res = await companyApi.create(payload);
        const newId = res.data.data?.id;
        if (newId) {
          flash('success', 'Company created successfully.');
          resetToInitial();
        }
      } else if (screen.isEdit && record) {
        await companyApi.update(record.id, payload);
        flash('success', 'Company updated successfully.');
        resetToInitial();
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })
        .response?.data?.message ?? 'Save failed. Please try again.';
      flash('error', msg);
    } finally {
      setSaving(false);
      setPendingSave(null);
    }
  };

  // ── Toolbar: Delete ───────────────────────────────────────────────────────────
  const handleDelete = () => setShowDeleteConfirm(true);

  const executeDelete = async () => {
    if (!record) return;
    setShowDeleteConfirm(false);
    setSaving(true);
    try {
      await companyApi.remove(record.id);
      flash('success', `Company '${record.name}' has been deleted.`);
      resetToInitial();
    } catch {
      flash('error', 'Delete failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── Toolbar: Print → open report preview ─────────────────────────────────────
  const handlePrint = () => {
    if (record) setPrintOpen(true);
  };

  // ── Toolbar: Exit ─────────────────────────────────────────────────────────────
  const handleExit = () => navigate(AppRoutes.DASHBOARD);

  // ── Search modal ──────────────────────────────────────────────────────────────
  const fetchCompanies = useCallback(async (
    search: string, page: number, pageSize: number,
  ) => {
    const res = await companyApi.getList({ search, pageNumber: page, pageSize });
    const d = res.data.data;
    return { items: d?.items ?? [], totalCount: d?.totalCount ?? 0 };
  }, []);

  const handleSearchSelect = (item: CompanyListDto) => {
    setSearchOpen(false);
    setLookupBusy(true);
    companyApi.getById(item.id)
      .then(res => { if (res.data.data) loadRecord(res.data.data); })
      .catch(() => flash('error', 'Failed to load company.'))
      .finally(() => setLookupBusy(false));
  };

  // ── Field disable logic ───────────────────────────────────────────────────────
  // Code field in form: always read-only (set via lookup bar only)
  // Other fields: editable only in NEW or EDIT mode
  const fieldsDisabled = screen.isInitial || screen.isView;

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full min-h-0">

      {/* ── Page header ── */}
      <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <Building2 size={18} className="text-brand-500 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <h1 className="text-sm font-bold text-gray-900 dark:text-gray-100">Company Maintenance</h1>
          <p className="text-xs text-gray-500 dark:text-gray-400">Manage company profile and settings</p>
        </div>
        {/* Mode badge — always visible, reflects current mode */}
        <ScreenModeBadge isNew={screen.isNew} isEdit={screen.isEdit} isView={screen.isView} />
      </div>

      {/* ── Toolbar ── */}
      <MasterToolbar
        {...screen.toolbar}
        saving={saving}
        loading={lookupBusy}
        onSave={handleSaveClick}
        onEdit={handleEdit}
        onPrint={handlePrint}
        onDelete={handleDelete}
        onRefresh={handleRefresh}
        onExit={handleExit}
      />

      {/* ── Flash message ── */}
      {flashMsg && (
        <div className={cn(
          'flex items-center gap-2 px-5 py-2.5 text-sm',
          flashMsg.type === 'success'
            ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-b border-emerald-200 dark:border-emerald-800'
            : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-b border-red-200 dark:border-red-800',
        )}>
          {flashMsg.type === 'success'
            ? <CheckCircle2 size={15} className="flex-shrink-0" />
            : <AlertTriangle size={15} className="flex-shrink-0" />}
          {flashMsg.text}
        </div>
      )}

      {/* ── Code lookup bar ── */}
      <div className="flex items-center gap-3 px-5 py-2 bg-gray-50 dark:bg-gray-800/40 border-b border-gray-200 dark:border-gray-700">
        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 w-14 flex-shrink-0">
          Code
        </span>
        <div className="flex items-center gap-2">
          <input
            ref={codeInputRef}
            type="text"
            value={codeInput}
            onChange={e => setCodeInput(e.target.value.toUpperCase())}
            onKeyDown={e => {
              if (e.key === 'Enter') { e.preventDefault(); handleCodeLookup(); }
            }}
            disabled={screen.isNew || screen.isEdit || saving}
            placeholder="Type code + Enter to search…"
            className={cn(
              'w-48 h-8 px-3 text-sm font-mono rounded-md border',
              'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100',
              'placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent',
              'disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed',
              'border-gray-300 dark:border-gray-600 transition-colors uppercase',
            )}
          />
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            disabled={screen.isNew || screen.isEdit || saving}
            className={cn(
              'h-8 w-8 flex items-center justify-center rounded-md border',
              'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800',
              'text-gray-500 dark:text-gray-400 hover:text-brand-600 hover:border-brand-400',
              'disabled:opacity-40 disabled:cursor-not-allowed transition-colors',
            )}
            title="Browse companies"
          >
            {lookupBusy ? <Spinner size="sm" /> : <Search size={14} />}
          </button>
        </div>

        {/* Record name display */}
        {record && (
          <div className="flex items-center gap-2 ml-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate max-w-xs">
              {record.name}
            </span>
            {!record.isActive && (
              <span className="px-1.5 py-0.5 rounded bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 text-[10px] font-bold uppercase tracking-wide">
                Inactive
              </span>
            )}
          </div>
        )}

        {screen.isInitial && (
          <span className="text-xs text-gray-400 dark:text-gray-500 ml-2 italic">
            Enter a company code and press Enter, or click the search icon to browse.
          </span>
        )}
      </div>

      {/* ── Main content — ALWAYS visible (blank when INITIAL) ── */}
      <div className="flex-1 overflow-auto">
        <form onSubmit={e => e.preventDefault()} noValidate>

          {/* Tabs — always visible */}
          <div className="flex gap-0 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-5 sticky top-0 z-10">
            {TABS.map(tab => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={cn(
                  'px-4 py-2.5 text-xs font-medium border-b-2 transition-colors whitespace-nowrap',
                  activeTab === tab
                    ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200',
                )}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="p-5 space-y-5 max-w-3xl">

            {/* ── General Information ── */}
            {activeTab === 'General' && (
              <Card>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-4">
                  General Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                  {/* Code — always read-only in form; entry is via lookup bar */}
                  <Input
                    label="Company Code"
                    {...register('code')}
                    disabled
                    placeholder="— enter via code field above —"
                    className="font-mono uppercase"
                    hint={screen.isNew ? 'Code locked after entry — cannot be changed.' : undefined}
                  />

                  <Input
                    label="Company Name *"
                    {...register('name')}
                    error={errors.name?.message}
                    disabled={fieldsDisabled}
                    placeholder="Full company name"
                  />

                  <Input
                    label="Legal Name"
                    {...register('legalName')}
                    error={errors.legalName?.message}
                    disabled={fieldsDisabled}
                    placeholder="Registered legal name (if different)"
                    className="sm:col-span-2"
                  />

                  {/* Status toggle */}
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Status
                    </label>
                    <label className={cn(
                      'inline-flex items-center gap-3',
                      fieldsDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                    )}>
                      <div className="relative">
                        <input
                          type="checkbox"
                          {...register('isActive')}
                          disabled={fieldsDisabled}
                          className="sr-only peer"
                        />
                        <div className="w-10 h-5 rounded-full bg-gray-200 dark:bg-gray-600 peer-checked:bg-emerald-500 peer-disabled:opacity-50 transition-colors" />
                        <div className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-5" />
                      </div>
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {isActive ? 'Active' : 'Inactive'}
                      </span>
                    </label>
                  </div>
                </div>
              </Card>
            )}

            {/* ── Contact Details ── */}
            {activeTab === 'Contact' && (
              <Card>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-4">
                  Contact Details
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input label="Address Line 1" {...register('addressLine1')}
                    error={errors.addressLine1?.message} disabled={fieldsDisabled}
                    placeholder="Street address" className="sm:col-span-2" />
                  <Input label="Address Line 2" {...register('addressLine2')}
                    error={errors.addressLine2?.message} disabled={fieldsDisabled}
                    placeholder="Suite, floor, unit, etc." className="sm:col-span-2" />
                  <Input label="City" {...register('city')}
                    error={errors.city?.message} disabled={fieldsDisabled} placeholder="City" />
                  <Input label="Country Code" {...register('countryCode')}
                    error={errors.countryCode?.message} disabled={fieldsDisabled}
                    placeholder="e.g. US, GB, MY" maxLength={3} className="uppercase" />
                  <Input label="Phone" {...register('phone')}
                    error={errors.phone?.message} disabled={fieldsDisabled}
                    placeholder="+1 555 000 0000" type="tel" />
                  <Input label="Email" {...register('email')}
                    error={errors.email?.message} disabled={fieldsDisabled}
                    placeholder="info@company.com" type="email" />
                  <Input label="Website" {...register('website')}
                    error={errors.website?.message} disabled={fieldsDisabled}
                    placeholder="https://www.company.com" className="sm:col-span-2" />
                </div>
              </Card>
            )}

            {/* ── Tax & Registration ── */}
            {activeTab === 'Tax & Registration' && (
              <Card>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-4">
                  Tax &amp; Registration
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input label="Registration Number" {...register('registrationNumber')}
                    error={errors.registrationNumber?.message} disabled={fieldsDisabled}
                    placeholder="Company registration / incorporation number"
                    className="sm:col-span-2" />
                  <Input label="Tax Number" {...register('taxNumber')}
                    error={errors.taxNumber?.message} disabled={fieldsDisabled}
                    placeholder="VAT / GST / EIN / TIN number"
                    className="sm:col-span-2" />
                </div>
              </Card>
            )}

            {/* ── Finance Settings ── */}
            {activeTab === 'Finance' && (
              <Card>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-4">
                  Finance Settings
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Controller name="baseCurrencyCode" control={control}
                    render={({ field }) => (
                      <Combobox label="Base Currency *" options={CURRENCY_OPTIONS}
                        value={field.value} onChange={field.onChange}
                        disabled={fieldsDisabled}
                        error={errors.baseCurrencyCode?.message}
                        placeholder="Select currency…" />
                    )}
                  />
                  <Controller name="fiscalYearStartMonth" control={control}
                    render={({ field }) => (
                      <Combobox label="Fiscal Year Start Month" options={MONTHS} clearable={false}
                        value={String(field.value)} onChange={v => field.onChange(Number(v))}
                        disabled={fieldsDisabled}
                        error={errors.fiscalYearStartMonth?.message} />
                    )}
                  />
                  <div className="sm:col-span-2">
                    <Controller name="timeZone" control={control}
                      render={({ field }) => (
                        <Combobox label="Time Zone" options={TIMEZONE_OPTIONS}
                          value={field.value ?? ''}
                          onChange={val => field.onChange(val || null)}
                          disabled={fieldsDisabled}
                          error={errors.timeZone?.message}
                          placeholder="Select time zone…" />
                      )}
                    />
                  </div>

                  {/* GAP-001 — full width, because the note under it is the part that matters. */}
                  <Controller name="chequeNumberMode" control={control}
                    render={({ field }) => (
                      <Combobox label="Cheque Numbering" options={CHEQUE_NUMBER_MODES} clearable={false}
                        className="sm:col-span-2"
                        value={field.value} onChange={field.onChange}
                        disabled={fieldsDisabled}
                        error={errors.chequeNumberMode?.message}
                        hint="Applies to bank accounts that have a registered cheque book. An account with no book is unaffected either way — there is nothing to take a number from, so the field stays free text. In both modes a number that is already used, voided or held by another document is refused." />
                    )}
                  />
                </div>
              </Card>
            )}

            {/* ── Audit Info ── */}
            {activeTab === 'Audit' && (
              <Card>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-4">
                  Audit Information
                </h3>
                {record ? (
                  <MasterAuditInfo
                    createdBy={record.createdBy}
                    createdByName={record.createdByName}
                    createdDate={record.createdDate}
                    modifiedBy={record.modifiedBy}
                    modifiedByName={record.modifiedByName}
                    modifiedDate={record.modifiedDate}
                  />
                ) : (
                  <p className="text-sm text-gray-400 dark:text-gray-500 italic">
                    Audit information will appear after the record is saved.
                  </p>
                )}
              </Card>
            )}

          </div>
        </form>
      </div>

      {/* ════════════════════════════════════════════════════════════════
          Modals
      ════════════════════════════════════════════════════════════════ */}

      {/* Search modal */}
      <MasterSearchModal<CompanyListDto>
        isOpen={searchOpen}
        onClose={() => setSearchOpen(false)}
        title="Browse Companies"
        columns={SEARCH_COLUMNS}
        fetchData={fetchCompanies}
        onSelect={handleSearchSelect}
      />

      {/* Create-new confirmation */}
      <ConfirmDialog
        isOpen={showNewConfirm !== null}
        variant="info"
        title="Create New Company"
        message={
          <span>
            Company code <strong className="font-mono">{showNewConfirm}</strong> does not exist.
            <br />Do you want to create a new record?
          </span>
        }
        confirmLabel="Yes, Create"
        cancelLabel="No"
        onConfirm={handleConfirmCreateNew}
        onCancel={() => setShowNewConfirm(null)}
      />

      {/* Save confirmation */}
      <ConfirmDialog
        isOpen={showSaveConfirm}
        variant="save"
        title={screen.isNew ? 'Save New Company' : 'Save Changes'}
        message={
          screen.isNew
            ? `Do you want to save this new company?`
            : `Do you want to save changes to company "${record?.name}"?`
        }
        confirmLabel="Yes, Save"
        cancelLabel="No"
        loading={saving}
        onConfirm={executeSave}
        onCancel={() => { setShowSaveConfirm(false); setPendingSave(null); }}
      />

      {/* Delete confirmation */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        variant="delete"
        title="Delete Company"
        message={
          <span>
            Do you want to delete company <strong>{record?.name}</strong>?
            <br />
            <span className="text-xs text-gray-400 dark:text-gray-500 mt-1 block">
              The record will be soft-deleted and can be restored by an administrator.
            </span>
          </span>
        }
        confirmLabel="Yes, Delete"
        cancelLabel="No"
        loading={saving}
        onConfirm={executeDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      {/* Print report preview */}
      {record && (
        <MasterReportPreview
          isOpen={printOpen}
          onClose={() => setPrintOpen(false)}
          title="Company Master Report"
        >
          <ReportHeader
            reportTitle="Company Master Report"
            printedBy={user?.fullName ?? user?.username}
            printedAt={new Date()}
            meta={[
              { label: 'Company Code', value: record.code },
              { label: 'Status', value: record.isActive ? 'Active' : 'Inactive' },
            ]}
          />

          <ReportSection
            title="General Information"
            fields={[
              { label: 'Company Code',  value: record.code },
              { label: 'Status',        value: record.isActive, display: record.isActive ? 'Active' : 'Inactive' },
              { label: 'Company Name',  value: record.name,      wide: true },
              { label: 'Legal Name',    value: record.legalName, wide: true },
            ]}
          />

          <ReportSection
            title="Contact Details"
            fields={[
              { label: 'Address Line 1', value: record.addressLine1, wide: true },
              { label: 'Address Line 2', value: record.addressLine2, wide: true },
              { label: 'City',           value: record.city },
              { label: 'Country Code',   value: record.countryCode },
              { label: 'Phone',          value: record.phone },
              { label: 'Email',          value: record.email },
              { label: 'Website',        value: record.website, wide: true },
            ]}
          />

          <ReportSection
            title="Tax & Registration"
            fields={[
              { label: 'Registration Number', value: record.registrationNumber, wide: true },
              { label: 'Tax Number',          value: record.taxNumber,          wide: true },
            ]}
          />

          <ReportSection
            title="Finance Settings"
            fields={[
              { label: 'Base Currency',          value: record.baseCurrencyCode },
              { label: 'Fiscal Year Start Month', value: MONTH_NAMES[record.fiscalYearStartMonth] },
              { label: 'Time Zone',              value: record.timeZone, wide: true },
            ]}
          />

          <ReportSection
            title="Audit Information"
            fields={[
              { label: 'Created By',   value: record.createdByName ?? record.createdBy },
              { label: 'Created Date', value: fmtDateTime(record.createdDate) },
              { label: 'Modified By',  value: record.modifiedByName ?? record.modifiedBy },
              { label: 'Modified Date',value: fmtDateTime(record.modifiedDate) },
            ]}
          />

          <ReportFooter />
        </MasterReportPreview>
      )}
    </div>
  );
};
